<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-cash-multiple me-2"></i>{{ $t('page.revenue', 'Revenue by Legal Source') }}</h4>
        </div>
        <div class="d-flex gap-2 align-items-center">
          <select v-model="period" class="hms-input" @change="loadAll" style="min-width:160px">
            <option value="last_30d">Last 30 days</option>
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
          <select v-model="scope" class="hms-input" @change="loadAll" style="min-width:160px">
            <option value="branch">This facility</option>
            <option value="county">All county facilities</option>
          </select>
          <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- HEADLINE -->
      <div class="hms-grid-3 mb-3">
        <div class="hms-kpi">
          <div class="hms-kpi-icon hms-kpi-blue"><i class="mdi mdi-cash"></i></div>
          <div>
            <div class="hms-kpi-num">KSh {{ formatNumber(totals.total_billed || 0) }}</div>
            <div class="hms-kpi-lbl">Total Billed</div>
            <div class="hms-kpi-sub">{{ totals.bill_count || 0 }} bills</div>
          </div>
        </div>
        <div class="hms-kpi">
          <div class="hms-kpi-icon hms-kpi-green"><i class="mdi mdi-arrow-down-bold-circle"></i></div>
          <div>
            <div class="hms-kpi-num">KSh {{ formatNumber(totals.retained_total || 0) }}</div>
            <div class="hms-kpi-lbl">Retained at Facility</div>
            <div class="hms-kpi-sub">FIF / SHA / Ntutu / OOP</div>
          </div>
        </div>
        <div class="hms-kpi">
          <div class="hms-kpi-icon hms-kpi-amber"><i class="mdi mdi-arrow-up-bold-circle"></i></div>
          <div>
            <div class="hms-kpi-num">KSh {{ formatNumber(totals.remitted_total || 0) }}</div>
            <div class="hms-kpi-lbl">Remitted to County Treasury</div>
            <div class="hms-kpi-sub">Finance Act / Public Health Act</div>
          </div>
        </div>
      </div>

      <!-- WATERFALL CHART -->
      <div class="hms-card mb-3">
        <div class="hms-card-head">Revenue waterfall by legal source</div>
        <div class="hms-card-body">
          <div v-if="!byAct.length" class="hms-muted text-center hms-pad">No revenue in selected period.</div>
          <div v-else>
            <div v-for="row in byAct" :key="row.source_code" class="hms-waterfall-row">
              <div class="hms-wf-meta">
                <div class="d-flex align-items-center gap-2">
                  <span class="hms-wf-dot" :style="{ background: row.color }"></span>
                  <strong>{{ row.source_name }}</strong>
                  <span v-if="row.retained == 1" class="hms-pill hms-pill-success">Retained</span>
                  <span v-else class="hms-pill hms-pill-muted">Remitted</span>
                </div>
                <div class="hms-muted-sm">{{ row.legal_basis }}</div>
              </div>
              <div class="hms-wf-bar-wrap">
                <div class="hms-wf-bar" :style="{ width: pct(row.total_amount) + '%', background: row.color }"></div>
              </div>
              <div class="hms-wf-amt">
                <strong>KSh {{ formatNumber(row.total_amount) }}</strong>
                <div class="hms-muted-sm">{{ row.bill_count }} bills · {{ pct(row.total_amount) }}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- LEGAL REFERENCES -->
      <div class="hms-card">
        <div class="hms-card-head">Legal references</div>
        <div class="hms-card-body p-0">
          <table class="hms-table">
            <thead><tr><th>Code</th><th>Name</th><th>Legal basis</th><th>Retention</th></tr></thead>
            <tbody>
              <tr v-for="s in sources" :key="s.source_id">
                <td><strong :style="{ color: s.color }">{{ s.code }}</strong></td>
                <td>{{ s.name }}</td>
                <td>{{ s.legal_basis }}</td>
                <td>
                  <span v-if="s.retained == 1" class="hms-pill hms-pill-success">Retained at facility</span>
                  <span v-else class="hms-pill hms-pill-muted">Remitted to County Treasury</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "RevenueWaterfall",
  components: { Navigation },
  data() {
    return {
      period: 'this_month',
      scope: 'branch',
      byAct: [], totals: {}, sources: []
    };
  },
  mounted() { this.loadSources(); this.loadAll(); },
  methods: {
    async loadSources() {
      try {
        const r = await executeJson({ function: 'getRevenueSources' }, hms);
        if (r.data && r.data.success) this.sources = (r.data.data && r.data.data.Sources) || [];
      } catch (e) { console.error(e); }
    },
    async loadAll() {
      try {
        const r = await executeJson({ function: 'getRevenueWaterfall', period: this.period, scope: this.scope }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.byAct = d.ByAct || [];
          this.totals = d.Totals || {};
        }
      } catch (e) { console.error(e); }
    },
    pct(amount) {
      const total = parseFloat(this.totals.total_billed || 0);
      if (total <= 0) return 0;
      return Math.round((parseFloat(amount) / total) * 100);
    },
    formatNumber(n) { const x = parseFloat(n || 0); return x.toLocaleString(undefined, { maximumFractionDigits: 0 }); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
@media (max-width: 992px) { .hms-grid-3 { grid-template-columns: 1fr; } }

.hms-kpi { display: flex; align-items: center; gap: 14px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; }
.hms-kpi-icon { width: 52px; height: 52px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 24px; }
.hms-kpi-blue  { background: #ebf8ff; color: #2b6cb0; }
.hms-kpi-green { background: #f0fff4; color: #276749; }
.hms-kpi-amber { background: #fffaf0; color: #b45309; }
.hms-kpi-num   { font-size: 22px; font-weight: 700; color: #1a202c; }
.hms-kpi-lbl   { font-size: 12px; color: #718096; text-transform: uppercase; letter-spacing: .5px; }
.hms-kpi-sub   { font-size: 12px; color: #4a5568; margin-top: 2px; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-waterfall-row { display: grid; grid-template-columns: 320px 1fr 200px; gap: 16px; align-items: center; padding: 10px 0; border-bottom: 1px solid #edf2f7; }
.hms-waterfall-row:last-child { border-bottom: none; }
.hms-wf-dot { display: inline-block; width: 12px; height: 12px; border-radius: 50%; }
.hms-wf-meta strong { font-size: 14px; color: #1a202c; }
.hms-wf-bar-wrap { background: #edf2f7; border-radius: 4px; height: 16px; overflow: hidden; }
.hms-wf-bar { height: 100%; transition: width .4s; }
.hms-wf-amt { text-align: right; }
.hms-wf-amt strong { font-size: 16px; color: #1a202c; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hms-table th { background: #f7fafc; padding: 10px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 9px 10px; border-bottom: 1px solid #edf2f7; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-input { padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-blue { background: #2b6cb0; color: #fff; }

.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
